import { useTranslation } from 'react-i18next';
import { useAdminImage } from '@/hooks/useAdminImage';

export default function HeroSection() {
  const { t } = useTranslation();

  const heroBg = useAdminImage(
    'home',
    'images.home.hero_bg',
    'https://readdy.ai/api/search-image?query=Ultra%20high%20resolution%20modern%20luxury%20medical%20clinic%20building%20exterior%2C%20sleek%20contemporary%20architecture%20with%20glass%20and%20white%20concrete%20facade%2C%20clean%20sharp%20lines%2C%20professional%20photography%2C%20crystal%20clear%20image%2C%20sharp%20focus%2C%20premium%20dental%20clinic%2C%20elegant%20signage%20with%20ESTEVA%20CLINIC%20text%20on%20building%20facade%2C%20bright%20daylight%2C%20blue%20sky%2C%20lush%20green%20trees%2C%20editorial%20architecture%20photography%2C%208K%20quality%2C%20no%20blur%2C%20no%20pixelation%2C%20perfectly%20sharp%20details&width=1920&height=1080&seq=hero-esteva-clean-2024&orientation=landscape'
  );

  return (
    <section className="relative w-full min-h-[100dvh] flex items-start justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt="Esteva Clinic Premium Architecture"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/35 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/40 to-transparent"></div>
        <div className="absolute inset-0" style={{background: 'radial-gradient(ellipse 80% 60% at 50% 55%, rgba(0,0,0,0.35) 0%, transparent 70%)'}}></div>
      </div>

      <div className="relative z-10 w-full text-center px-5 md:px-10 pt-20 md:pt-24 pb-6 md:pb-10">
        <div className="max-w-4xl mx-auto">
          <span
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-white text-[10px] md:text-xs tracking-[0.2em] uppercase font-label mb-6 md:mb-8 backdrop-blur-sm"
            style={{background: 'rgba(126,34,206,0.45)', border: '1px solid rgba(192,132,252,0.55)'}}
          >
            <i className="ri-award-line text-xs"></i>
            {t('hero.badge')}
          </span>

          <h1 className="font-heading text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-bold text-white leading-[0.95] tracking-tight">
            {t('hero.title_line1')}
          </h1>
          <h1 className="font-heading text-4xl sm:text-6xl md:text-7xl lg:text-8xl italic font-light text-white leading-[1.1] mt-1 md:mt-2 tracking-tight">
            {t('hero.title_line2')}
          </h1>
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="/booking"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-xs tracking-[0.12em] uppercase font-label font-semibold hover:scale-[1.02] transition-all duration-300 cursor-pointer whitespace-nowrap text-white"
              style={{background: 'rgba(126,34,206,0.85)', border: '1px solid rgba(192,132,252,0.5)'}}
            >
              {t('hero.cta')}
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-transparent text-white rounded-full text-xs tracking-[0.12em] uppercase font-label font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-[1.02]"
              style={{border: '1px solid rgba(192,132,252,0.6)'}}
            >
              <i className="ri-price-tag-3-line text-sm"></i>
              {t('hero.discover_prices')}
            </a>
          </div>

          <div
            className="mt-8 md:mt-10 mx-auto max-w-sm md:max-w-none grid grid-cols-3 gap-2 px-4 py-4 rounded-2xl backdrop-blur-md md:flex md:flex-wrap md:items-center md:justify-center md:gap-10 md:p-0 md:rounded-none md:backdrop-blur-none md:bg-transparent md:border-0"
            style={{ background: 'rgba(0,0,0,0.28)', border: '1px solid rgba(192,132,252,0.2)' }}
          >
            {[
              { icon: 'ri-user-heart-line', valueKey: 'hero.stat1.value', labelKey: 'hero.stat1.label' },
              { icon: 'ri-medal-line', valueKey: 'hero.stat2.value', labelKey: 'hero.stat2.label' },
              { icon: 'ri-shield-check-line', valueKey: 'hero.stat3.value', labelKey: 'hero.stat3.label' },
            ].map((stat) => (
              <div key={stat.labelKey} className="flex flex-col md:flex-row items-center md:gap-2.5 gap-1.5 text-center md:text-left">
                <div className="w-8 h-8 md:w-7 md:h-7 flex items-center justify-center rounded-full flex-shrink-0" style={{background: 'rgba(126,34,206,0.35)', border: '1px solid rgba(192,132,252,0.4)'}}>
                  <i className={`${stat.icon} text-sm md:text-xs text-white/90`}></i>
                </div>
                <div className="md:text-left">
                  <div className="text-white font-heading text-base md:text-base font-bold leading-tight">{t(stat.valueKey)}</div>
                  <div className="text-white/55 font-label text-[9px] md:text-[10px] tracking-[0.1em] uppercase leading-tight mt-0.5 md:mt-0">{t(stat.labelKey)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <a href="#services" className="w-8 h-8 flex items-center justify-center cursor-pointer text-white/50 hover:text-white/90 transition-colors">
          <i className="ri-arrow-down-line text-lg"></i>
        </a>
      </div>
    </section>
  );
}